import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, MapPin, MapPinCheck, Coffee, Square, Camera, CheckCircle2 } from "lucide-react";
import { PhoneShell, Spot } from "../Frames";

const Install = () => (
  <div className="w-full h-full bg-[#0B1120] home-grid relative text-white flex flex-col text-left">
    <div className="h-9" />
    <div className="flex-1 flex flex-col items-center justify-center gap-3 pb-40">
      <img src="/crossjobs-mark.png" className="h-20 w-auto" alt="" />
      <p className="font-display font-black text-[24px] uppercase tracking-tight">Cross Jobs</p>
      <p className="text-[12px] text-slate-400">Sign in to see your jobs</p>
    </div>
    <motion.div initial={{ y: 260 }} animate={{ y: 0 }} transition={{ delay: 0.4, type: "spring", damping: 22 }}
      className="absolute bottom-0 inset-x-0 rounded-t-3xl bg-white text-[#0F172A] p-5 space-y-3">
      <div className="mx-auto w-10 h-1 rounded-full bg-slate-300" />
      <div className="flex items-center gap-3">
        <img src="/icon-cj-192.png" className="w-12 h-12 rounded-xl" alt="" />
        <div><p className="font-bold">Install Cross Jobs</p><p className="text-[11px] text-slate-500">Adds an icon to your home screen</p></div>
      </div>
      <Spot on>
        <div className="h-11 rounded-full bg-[#1E3A5F] text-white font-semibold flex items-center justify-center gap-2"><Download className="w-4 h-4" /> Install app</div>
      </Spot>
      <p className="text-[10px] text-slate-500 text-center">On iPhone: Share, then Add to Home Screen</p>
    </motion.div>
  </div>
);

const Job = () => (
  <PhoneShell title="My jobs">
    <p className="text-[10px] font-mono text-slate-500 mb-2">TODAY · 11 JUN</p>
    <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-2.5 shadow-sm">
      <div className="flex justify-between items-center">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold">Standard</span>
        <span className="text-[10px] text-slate-500 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" />Not started</span>
      </div>
      <p className="font-display font-bold text-[17px] leading-tight">Lot 12 Banksia Cres</p>
      <p className="text-[12px] text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />Armstrong Creek VIC 3217</p>
      <p className="text-[12px]"><span className="text-slate-500">Client · </span>Coastline Homes</p>
      <div className="rounded-lg bg-slate-50 p-2.5 text-[11px] text-slate-600">Level 5 finish to living, hallway and garage. Cornice in all bedrooms.</div>
      <Spot on>
        <div className="h-12 rounded-xl bg-[#EA580C] text-white font-bold flex items-center justify-center gap-2 shadow-lg ring-4 ring-[#EA580C]/20"><MapPinCheck className="w-5 h-5" /> Check in on-site</div>
      </Spot>
    </div>
    <div className="mt-3 rounded-xl bg-white border border-slate-200 p-3 opacity-70">
      <p className="text-[10px] font-mono text-slate-500">12 JUN</p>
      <p className="font-semibold text-[12px]">27 Wattle St, Highton</p>
    </div>
  </PhoneShell>
);

const hms = (s) => [Math.floor(s / 3600), Math.floor(s / 60) % 60, s % 60].map((n) => String(n).padStart(2, "0")).join(":");

export const Clock = () => {
  const [s, setS] = useState(2 * 3600 + 14 * 60 + 37);
  useEffect(() => {
    const id = setInterval(() => setS((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <PhoneShell title="Clock">
      <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" /><span className="font-semibold">On shift</span>
          <span className="ml-auto text-[10px] font-mono text-slate-500">since 06:48</span>
        </div>
        <p className="text-[11px] text-slate-500">Lot 12 Banksia Cres</p>
        <Spot on><p className="font-mono text-[40px] font-bold tracking-tight text-center">{hms(s)}</p></Spot>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] text-slate-500">Break</p><p className="font-mono font-bold text-[16px]">00:30:00</p></div>
          <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] text-slate-500">This week</p><p className="font-mono font-bold text-[16px]">24.5h</p></div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[12px]">
          <div className="h-11 rounded-xl border border-slate-200 font-semibold flex items-center justify-center gap-1.5"><Coffee className="w-4 h-4" /> Start break</div>
          <div className="h-11 rounded-xl bg-[#1E3A5F] text-white font-semibold flex items-center justify-center gap-1.5"><Square className="w-3.5 h-3.5" /> Clock off</div>
        </div>
      </div>
    </PhoneShell>
  );
};

const SHOTS = [["Before", "from-stone-400 to-stone-500"], ["Before", "from-slate-400 to-stone-500"], ["After", "from-stone-100 to-stone-200"], ["After", "from-slate-100 to-stone-200"]];
const Photos = () => (
  <PhoneShell title="Photos">
    <p className="text-[11px] text-slate-500 mb-2">Lot 12 Banksia Cres · 4 photos</p>
    <div className="grid grid-cols-2 gap-2">
      {SHOTS.map(([k, g], i) => (
        <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 * i }}
          className={`relative aspect-square rounded-xl bg-gradient-to-br ${g} overflow-hidden`}>
          <div className="absolute inset-0 home-plaster" />
          <span className="absolute left-2 top-2 rounded-full bg-black/60 text-white text-[9px] font-bold px-2 py-0.5 uppercase">{k}</span>
          <Camera className="absolute right-2 bottom-2 w-4 h-4 text-white/80" />
        </motion.div>
      ))}
    </div>
    <div className="mt-3 rounded-lg bg-white border border-slate-200 p-2.5">
      <p className="text-[10px] text-slate-500 mb-1">Uploading photos…</p>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <motion.div initial={{ width: "8%" }} animate={{ width: "100%" }} transition={{ duration: 2.6 }} className="h-full bg-[#EA580C]" />
      </div>
    </div>
    <Spot on className="mt-3">
      <div className="h-12 rounded-xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2"><CheckCircle2 className="w-5 h-5" /> Mark job complete</div>
    </Spot>
  </PhoneShell>
);

const STEPS = [Install, Job, Clock, Photos];
export const PhoneScreens = ({ step }) => {
  const S = STEPS[step];
  return <S />;
};
